/**
 * Phase 9.4.2 — Compliance error toast helper.
 *
 * Public surface:
 *   showComplianceError({ err, locale, navigate, role })
 *
 * When a publish/schedule action returns 409 with
 * `detail.compliance_violations: string[]`, render a Sonner error toast
 * that:
 *   - lists the (humanised) first ~3 violations,
 *   - exposes a "Fix in Settings" CTA that deep-links to
 *     /app/settings/legal#field=imprint_de,privacy_policy_de,…  so the
 *     legal editor can focus the first missing field on mount.
 *
 * If the user's role can't write to legal docs (only owner/admin can —
 * `tenants.write` permission), the CTA is hidden. Analyst/producer
 * managers just see the violation list.
 *
 * If the 409 has no compliance_violations array, fall back to a plain
 * toast.error with the server's message.
 */
import { toast } from "sonner";

// Roles allowed to edit /settings/legal (mirrors backend `tenants.write`).
const LEGAL_WRITE_ROLES = new Set(["owner", "admin"]);

// Friendly labels for the most common violation keys. Anything not in
// here is shown verbatim (the machine key is still informative).
const LABELS = {
  de: {
    imprint_de: "Impressum (DE)",
    imprint_en: "Impressum (EN)",
    privacy_policy_de: "Datenschutzerklärung (DE)",
    privacy_policy_en: "Datenschutzerklärung (EN)",
    "data_controller.name": "Verantwortlicher — Name",
    "data_controller.address": "Verantwortlicher — Adresse",
    "data_controller.email": "Verantwortlicher — E-Mail",
    "data_controller.phone": "Verantwortlicher — Telefon",
    consent_text_lead_form_de: "Einwilligungstext Lead-Formular (DE)",
    consent_text_lead_form_en: "Lead-form consent text (EN)",
  },
  en: {
    imprint_de: "Imprint (DE)",
    imprint_en: "Imprint (EN)",
    privacy_policy_de: "Privacy Policy (DE)",
    privacy_policy_en: "Privacy Policy (EN)",
    "data_controller.name": "Data Controller — Name",
    "data_controller.address": "Data Controller — Address",
    "data_controller.email": "Data Controller — Email",
    "data_controller.phone": "Data Controller — Phone",
    consent_text_lead_form_de: "Lead-form consent text (DE)",
    consent_text_lead_form_en: "Lead-form consent text (EN)",
  },
};

const COPY = {
  de: {
    title: "Compliance-Verstöße erkannt",
    title_singular: "Compliance-Verstoß erkannt",
    body_prefix: "Fehlende Pflichtfelder:",
    more: (n) => `… und ${n} weitere`,
    cta: "In Einstellungen beheben",
    generic_fallback: "Veröffentlichung fehlgeschlagen.",
  },
  en: {
    title: "Compliance violations detected",
    title_singular: "Compliance violation detected",
    body_prefix: "Missing required fields:",
    more: (n) => `… and ${n} more`,
    cta: "Fix in Settings",
    generic_fallback: "Publish failed.",
  },
};

export function labelFor(key, locale = "de") {
  const lng = LABELS[locale] ? locale : "de";
  return LABELS[lng][key] || key;
}

/**
 * Extract `compliance_violations` from an axios error.
 * Backend shape: `{ detail: { error, compliance_violations: [...] } }`.
 */
export function extractViolations(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return null;
  const arr = detail.compliance_violations;
  if (Array.isArray(arr) && arr.length > 0) return arr;
  return null;
}

/**
 * Show a compliance-aware error toast.
 *
 * @param {Object}   opts
 * @param {Error}    opts.err       Axios error
 * @param {string}   opts.locale    "de" | "en"
 * @param {Function} opts.navigate  react-router navigate(path)
 * @param {string}   opts.role      Current user role (e.g. "manager")
 * @returns {{kind: "compliance"|"generic", violations?: string[]}} for tests
 */
export function showComplianceError({ err, locale = "de", navigate, role }) {
  const lng = COPY[locale] ? locale : "de";
  const c = COPY[lng];
  const violations = extractViolations(err);

  if (!violations) {
    const msg = err?.response?.data?.detail?.error || c.generic_fallback;
    toast.error(msg, { duration: 6000 });
    return { kind: "generic" };
  }

  const labeled = violations.map((v) => labelFor(v, lng));
  const head = labeled.slice(0, 3);
  const rest = labeled.length - head.length;
  const list = head.join(" · ") + (rest > 0 ? ` ${c.more(rest)}` : "");
  const title = violations.length === 1 ? c.title_singular : c.title;
  const canFix = LEGAL_WRITE_ROLES.has(role) && typeof navigate === "function";

  const description = `${c.body_prefix} ${list}`;

  toast.error(title, {
    description,
    duration: 8000,
    // Sonner accepts a custom { action: { label, onClick } } prop.
    action: canFix
      ? {
          label: c.cta,
          onClick: () => {
            const hash = `#field=${encodeURIComponent(violations.join(","))}`;
            navigate(`/app/settings/legal${hash}`);
          },
        }
      : undefined,
    // For e2e / jest: a stable data-testid hook via the optional id.
    id: "compliance-error-toast",
  });

  return { kind: "compliance", violations };
}
